import { useCallback, useEffect, useMemo, useState } from 'react'
import { getOverflowDirections } from 'lib/util/scroll'
import throttle from 'lib/util/throttle'
import useResizeObserver from './useResizeObserver'

const useScrollPrompt = (ref, threshold = 20) => {
  const [hasOverflow, setHasOverflow] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [hasPrompted, setHasPrompted] = useState(false)
  const [overflowDirection, setOverflowDirection] = useState('vertical')

  /**
   * On refresh - called on resize observation or scroll. Using
   * throttle to reduce the amount of calls
   */
  const onRefresh = useCallback(
    throttle(() => {
      const scrollContainer = ref.current

      if (!scrollContainer) {
        return
      }

      const { bottom, left, right, top } = getOverflowDirections(
        scrollContainer,
        threshold
      )

      const hasOverflow = top || right || bottom || left

      setHasOverflow(hasOverflow)
      setOverflowDirection(top || bottom ? 'vertical' : 'horizontal')

      if (!hasOverflow) {
        return
      }

      const scrollDistance = Math.max(
        scrollContainer.scrollTop,
        scrollContainer.scrollLeft
      )

      if (scrollDistance >= threshold) {
        setHasScrolled(true)
      }
    }, 50),
    []
  )

  // Add the resize observer
  useResizeObserver(ref, onRefresh, !hasPrompted)

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    if (!ref?.current) {
      // Defensive
      return
    }

    ref.current.addEventListener('scroll', onRefresh)
    onRefresh()

    return () => {
      if (!ref.current) {
        return
      }

      ref.current.removeEventListener('scroll', onRefresh)
    }
  }, [])

  /**
   * Effect: When the `hasScrolled` changes and its true,
   * apply the `hasPrompted` meaning there was overflow and
   * the user scrolled past the threshold
   */
  useEffect(() => {
    if (!hasScrolled) {
      return
    }

    ref.current.removeEventListener('scroll', onRefresh)
    setHasPrompted(true)
  }, [hasScrolled])

  /**
   * Memo: Whether the scroll prompt should show
   */
  const shouldShow = useMemo(
    () => hasOverflow && !hasPrompted,
    [hasOverflow, hasPrompted]
  )

  const ctx = useMemo(
    () => ({
      shouldShow,
      overflowDirection
    }),
    [shouldShow, overflowDirection]
  )

  return ctx
}

// Export
export default useScrollPrompt
